// Global photo-upload queue.
//
// Photo uploads used to run in a `for...of` loop inside the visit/job component:
// strictly one at a time, and abandoned the moment that component unmounted
// (navigating away / closing the panel), losing every not-yet-uploaded photo.
//
// This provider hoists the upload work to the app root so it OUTLIVES the
// screen that started it. A technician can queue 60 photos on a visit and then
// move around the portal while they upload in the background, tracked by the
// floating <UploadProgressWidget />. Uploads run CONCURRENTLY (up to
// CONCURRENCY at once) and each file is compressed client-side first.
//
// Scope of resilience (by design): the queue survives in-app navigation but
// NOT a full tab close / refresh — the File objects live in memory only, there
// is no service worker / IndexedDB persistence. A beforeunload guard warns the
// user before they can discard an in-progress queue.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { uploadJobPhoto } from "../api/jobs";
import { uploadVisitPhoto } from "../api/visits";
import { uploadKbDocument } from "../api/knowledgeBase";
import { compressImage } from "../utils/imageCompress";

// iOS Safari caps per-tab memory hard: decoding/uploading several full-res
// photos at once overruns it and requests start failing (this is why bulk
// upload failed on iPhone but not desktop). Upload one at a time on iOS so only
// one photo is ever decoded in memory; desktop can afford more parallelism.
const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as "MacIntel" but has a touch screen
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
const CONCURRENCY = IS_IOS ? 1 : 3;

const MAX_ATTEMPTS = 4; // initial try + 3 retries
const RETRY_BASE_MS = 800; // backoff base; grows ~2^n with jitter

const INVALIDATE_DEBOUNCE_MS = 700; // coalesce refetches during a burst
const AUTO_CLEAR_MS = 5000; // clear a fully-successful queue after this idle time

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// A failure worth retrying: network errors (no HTTP response) and transient
// server responses (429 / 5xx). A 4xx like 401/403/404/400 is permanent — the
// photo won't succeed on retry, so fail it immediately.
function isRetryable(err) {
  const status = err?.response?.status;
  if (status == null) return true; // network / timeout / cancelled
  return status === 429 || status >= 500;
}

const UploadQueueContext = createContext(null);

let _seq = 0;
const nextId = () => `up_${Date.now()}_${_seq++}`;

const targetKey = (t) => `${t.kind}:${t.id}`;

export function UploadQueueProvider({ children }) {
  const queryClient = useQueryClient();

  // tasksRef is the worker's source of truth (mutated in place); `tasks` mirrors
  // it for rendering. Each task: { id, target:{kind,id,label}, name, status, error }
  // status ∈ "pending" | "uploading" | "done" | "error".
  const tasksRef = useRef([]);
  const [tasks, setTasks] = useState([]);
  const activeRef = useRef(0);
  const pumpRef = useRef(null);
  const invalidateTimersRef = useRef({});
  const autoClearTimerRef = useRef(null);

  // Bumped per completed photo, keyed by target — mounted detail views watch
  // their own target's count to re-fetch photos when their batch lands.
  const [completions, setCompletions] = useState({});

  const sync = useCallback(() => setTasks(tasksRef.current.slice()), []);

  const invalidateTarget = useCallback(
    (target) => {
      if (target.kind === "kb") {
        queryClient.invalidateQueries({ queryKey: ["kb"] });
      } else if (target.kind === "job") {
        // prefix match covers ["jobs", jobId] too
        queryClient.invalidateQueries({ queryKey: ["jobs"] });
      } else {
        // prefix match covers ["visits", jobId] too
        queryClient.invalidateQueries({ queryKey: ["visits"] });
      }
    },
    [queryClient]
  );

  const scheduleInvalidate = useCallback(
    (target) => {
      const key = targetKey(target);
      clearTimeout(invalidateTimersRef.current[key]);
      invalidateTimersRef.current[key] = setTimeout(() => {
        invalidateTarget(target);
      }, INVALIDATE_DEBOUNCE_MS);
    },
    [invalidateTarget]
  );

  const runTask = useCallback(
    async (task) => {
      // Compress once up front (best-effort — falls back to the original file
      // internally, never throws out of here).
      let file = task.file;
      try {
        // Documents (kind "kb") are uploaded as-is — only photos are compressed.
        if (task.target.kind !== "kb") file = await compressImage(task.file);
      } catch {
        file = task.file;
      }

      const upload = () =>
        task.target.kind === "kb"
          ? uploadKbDocument(task.target.id, file)
          : task.target.kind === "job"
          // Job-level photos carry no before/after stage — that is a property
          // of a site visit, not of the job.
          ? uploadJobPhoto(task.target.id, file)
          : uploadVisitPhoto(task.target.id, file, task.stage);

      // Retry transient failures automatically with exponential backoff + jitter
      // so a stray error self-heals instead of needing a manual retry tap.
      let lastErr = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        task.attempts = attempt;
        try {
          await upload();
          task.status = "done";
          task.error = null;
          task.file = null; // release the (possibly large) blob once uploaded
          scheduleInvalidate(task.target);
          const key = targetKey(task.target);
          setCompletions((c) => ({ ...c, [key]: (c[key] || 0) + 1 }));
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (attempt < MAX_ATTEMPTS && isRetryable(err)) {
            sync(); // reflect the in-progress retry in the widget
            await sleep(RETRY_BASE_MS * 2 ** (attempt - 1) + Math.random() * RETRY_BASE_MS);
          } else {
            break;
          }
        }
      }

      if (lastErr) {
        task.status = "error";
        task.error =
          lastErr?.response?.data?.detail ?? lastErr?.message ?? "Upload failed";
      }

      activeRef.current -= 1;
      sync();
      pumpRef.current?.();
    },
    [scheduleInvalidate, sync]
  );

  const pump = useCallback(() => {
    while (activeRef.current < CONCURRENCY) {
      const task = tasksRef.current.find((t) => t.status === "pending");
      if (!task) break;
      task.status = "uploading";
      activeRef.current += 1;
      runTask(task);
    }
    sync();
  }, [runTask, sync]);

  pumpRef.current = pump;

  // ⚠️ `stage` rides on the TASK, not on `target` — `targetKey` is `kind:id`
  // and drives both the debounced cache invalidation and the `completions`
  // counter that mounted panels watch to refetch. Folding stage into the target
  // would split that key and silently break the refresh wiring.
  const enqueuePhotos = useCallback(
    (target, files, { stage } = {}) => {
      const list = Array.from(files || []);
      if (!list.length) return;
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
        autoClearTimerRef.current = null;
      }
      const added = list.map((file) => ({
        id: nextId(),
        target,
        stage,
        name: file.name || "photo",
        file,
        status: "pending",
        error: null,
        attempts: 0,
      }));
      tasksRef.current = tasksRef.current.concat(added);
      pump();
    },
    [pump]
  );

  const retryFailed = useCallback(() => {
    let changed = false;
    for (const t of tasksRef.current) {
      if (t.status === "error" && t.file) {
        t.status = "pending";
        t.error = null;
        changed = true;
      }
    }
    if (changed) pump();
  }, [pump]);

  const clearCompleted = useCallback(() => {
    tasksRef.current = tasksRef.current.filter((t) => t.status !== "done");
    sync();
  }, [sync]);

  const dismissAll = useCallback(() => {
    // Only drop settled tasks; leave anything still in flight.
    tasksRef.current = tasksRef.current.filter(
      (t) => t.status === "pending" || t.status === "uploading"
    );
    sync();
  }, [sync]);

  const active = tasks.some((t) => t.status === "pending" || t.status === "uploading");
  const hasErrors = tasks.some((t) => t.status === "error");

  // Warn before a tab close/refresh discards an in-progress queue.
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  // Auto-clear a queue that finished with no errors, so the widget disappears.
  useEffect(() => {
    if (active || hasErrors || tasks.length === 0) return undefined;
    autoClearTimerRef.current = setTimeout(() => {
      tasksRef.current = tasksRef.current.filter((t) => t.status === "error");
      setTasks(tasksRef.current.slice());
    }, AUTO_CLEAR_MS);
    return () => {
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
    };
  }, [active, hasErrors, tasks.length]);

  const value = {
    enqueuePhotos,
    // Same queue, any file kind — target = { kind: "kb", id: folderId }.
    enqueueFiles: enqueuePhotos,
    tasks,
    completions,
    retryFailed,
    clearCompleted,
    dismissAll,
  };

  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}

export function useUploadQueue() {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) throw new Error("useUploadQueue must be used within an UploadQueueProvider");
  return ctx;
}
